import { useEffect, useState } from "react";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import Tooltip from "@mui/material/Tooltip";
import { BarChart3, Info } from "lucide-react";
import Typography from "../Typography";
import { ChevronDown, ChevronUp } from "../ui/icons";
import { useLayers } from "../../context/LayersContext";
import { TreeViewContainer } from "./styles";
import type { VoltageOption, SubLayerRef } from "../../global/constants/layers";
import StyledCheckbox from "../ui/styled/styled-checkbox";

interface Layer {
  title: string;
  layerName?: string;
  voltageOptions?: VoltageOption[];
  voltageGroupLabel?: string;
  voltageSubLayers?: SubLayerRef[];
  subLayers?: SubLayerRef[];
  subLayerGroupLabel?: string;
  info?: string;
  chartButtonTooltip?: string;
  children?: Layer[];
}

interface TreeNode {
  value: string;
  label: string;
  layerName?: string;
  voltageOptions?: VoltageOption[];
  voltageGroupLabel?: string;
  voltageSubLayers?: SubLayerRef[];
  subLayers?: SubLayerRef[];
  subLayerGroupLabel?: string;
  info?: string;
  chartButtonTooltip?: string;
  children?: TreeNode[];
}

export interface CheckboxTreeViewProps {
  data: Layer[];
  onVoltageFilterChange?: (
    layerName: string,
    values: (number | string)[]
  ) => void;
  /** Chamado pelo botão de gráfico das camadas que definem `chartButtonTooltip`. */
  onChartClick?: (layerName: string) => void;
}

interface VoltageFilterPanelProps {
  layerName: string;
  layerTitle: string;
  options: VoltageOption[];
  groupLabel?: string;
  voltageSubLayers?: SubLayerRef[];
  checked: string[];
  voltageChecked: Record<string, Set<number | string>>;
  onVoltageToggle: (
    layerName: string,
    layerTitle: string,
    value: number | string,
    allValues: (number | string)[]
  ) => void;
  setChecked: (updated: string[]) => void;
  addNewLayer: (updated: string[]) => void;
}

interface SubLayerPanelProps {
  subLayers: SubLayerRef[];
  groupLabel?: string;
  checked: string[];
  setChecked: (updated: string[]) => void;
  addNewLayer: (updated: string[]) => void;
}

interface TreeNodeComponentProps {
  node: TreeNode;
  checked: string[];
  voltageChecked: Record<string, Set<number | string>>;
  onCheck: (node: TreeNode, newValue: boolean) => void;
  onVoltageToggle: (
    layerName: string,
    layerTitle: string,
    value: number | string,
    allValues: (number | string)[]
  ) => void;
  setChecked: (updated: string[]) => void;
  addNewLayer: (updated: string[]) => void;
  onChartClick?: (layerName: string) => void;
}

function mapLayerToTreeNode(layer: Layer): TreeNode {
  // Category (group) nodes carry `children`; leaf/layer nodes don't. A category's
  // title can collide with a leaf's title (e.g. a category containing a single
  // layer of the same name), which would give two TreeItems the same nodeId and
  // make MUI's TreeView hang when expanding. Prefix category nodeIds to keep them
  // unique while leaving the displayed label untouched.
  const isCategory = !!layer.children?.length;
  return {
    value: isCategory ? `__category__:${layer.title}` : layer.title,
    label: layer.title,
    layerName: layer.layerName,
    voltageOptions: layer.voltageOptions,
    voltageGroupLabel: layer.voltageGroupLabel,
    voltageSubLayers: layer.voltageSubLayers,
    subLayers: layer.subLayers,
    subLayerGroupLabel: layer.subLayerGroupLabel,
    info: layer.info,
    chartButtonTooltip: layer.chartButtonTooltip,
    children: layer.children?.map(mapLayerToTreeNode),
  };
}

function leafIds(node: TreeNode): string[] {
  if (!node.children || node.children.length === 0) {
    return [node.value];
  }
  return node.children.flatMap(leafIds);
}

function allLeafAndSubLayerIds(node: TreeNode): string[] {
  if (!node.children || node.children.length === 0) {
    const subLayerTitles = [
      ...(node.subLayers?.map((s) => s.layerTitle) ?? []),
      ...(node.voltageSubLayers?.map((s) => s.layerTitle) ?? []),
    ];
    return [node.value, ...subLayerTitles];
  }
  return node.children.flatMap(allLeafAndSubLayerIds);
}

function leafNodes(layers: Layer[]): Layer[] {
  return layers.flatMap((layer) => {
    if (layer.children && layer.children.length > 0) {
      return leafNodes(layer.children);
    }
    return [layer];
  });
}

function hasActiveDescendantFilter(
  node: TreeNode,
  checked: string[],
  voltageChecked: Record<string, Set<number | string>>
): boolean {
  if (!node.children || node.children.length === 0) {
    const subLayerTitles = [
      ...(node.subLayers?.map((s) => s.layerTitle) ?? []),
      ...(node.voltageSubLayers?.map((s) => s.layerTitle) ?? []),
    ];
    if (checked.includes(node.value)) {
      // Pai marcado mas nem todos os subLayers estão — seleção parcial
      return subLayerTitles.length > 0 && !subLayerTitles.every((t) => checked.includes(t));
    }
    return (
      (!!node.layerName && (voltageChecked[node.layerName]?.size ?? 0) > 0) ||
      subLayerTitles.some((t) => checked.includes(t))
    );
  }
  return node.children.some((child) =>
    hasActiveDescendantFilter(child, checked, voltageChecked)
  );
}

function descendantLayerNames(node: TreeNode): string[] {
  if (!node.children || node.children.length === 0) {
    return node.layerName ? [node.layerName] : [];
  }
  return node.children.flatMap(descendantLayerNames);
}

function VoltageFilterPanel({
  layerName,
  layerTitle,
  options,
  groupLabel,
  voltageSubLayers,
  checked,
  voltageChecked,
  onVoltageToggle,
  setChecked,
  addNewLayer,
}: VoltageFilterPanelProps) {
  const allValues = options.map((o) => o.value);
  const parentChecked = checked.includes(layerTitle);
  const selected = parentChecked
    ? new Set<number | string>(allValues)
    : voltageChecked[layerName] ?? new Set<number | string>();

  return (
    <div className="pl-3 pb-0.5">
      <Typography variant="body4" className="text-gray-500 block mb-0.5">
        {groupLabel ?? "Tensão"}
      </Typography>
      {options.map((opt) => (
        <div key={String(opt.value)} className="flex items-center h-6 py-2">
          <StyledCheckbox
            checked={selected.has(opt.value)}
            onCheckedChange={() =>
              onVoltageToggle(layerName, layerTitle, opt.value, allValues)
            }
            onClick={(e) => e.stopPropagation()}
            className="p-0.25 mr-0.5"
          />
          {opt.color && (
            <span
              className="inline-block w-3 h-0.75 rounded-sm mr-0.75 flex-shrink-0"
              style={{ backgroundColor: opt.color }}
            />
          )}
          <Typography variant="body4">{opt.label}</Typography>
        </div>
      ))}
      {voltageSubLayers?.map((sub) => {
        const isSubChecked = checked.includes(sub.layerTitle);
        const toggle = () => {
          const isCurrentlyChecked = checked.includes(sub.layerTitle);
          const updated = isCurrentlyChecked
            ? checked.filter((id) => id !== sub.layerTitle && id !== layerTitle)
            : [...checked.filter((id) => id !== layerTitle), sub.layerTitle];
          setChecked(updated);
          addNewLayer(updated);
        };
        return (
          <div key={sub.layerTitle} className="flex items-center h-6">
            <StyledCheckbox
              checked={isSubChecked}
              onCheckedChange={toggle}
              onClick={(e) => e.stopPropagation()}
              className="p-0.25 mr-0.5"
            />
            {sub.color && (
              <span
                className="inline-block w-3 h-0.75 rounded-sm mr-0.75 flex-shrink-0"
                style={{ backgroundColor: sub.color }}
              />
            )}
            <Typography variant="body4">{sub.displayTitle}</Typography>
          </div>
        );
      })}
    </div>
  );
}

function SubLayerPanel({
  subLayers,
  groupLabel,
  checked,
  setChecked,
  addNewLayer,
}: SubLayerPanelProps) {
  return (
    <div className="pl-3 pb-0.5">
      {groupLabel && (
        <Typography variant="body4" className="text-gray-500 block mb-0.5">
          {groupLabel}
        </Typography>
      )}
      {subLayers.map((sub) => {
        const isChecked = checked.includes(sub.layerTitle);
        const toggle = () => {
          const isCurrentlyChecked = checked.includes(sub.layerTitle);
          const updated = isCurrentlyChecked
            ? checked.filter((id) => id !== sub.layerTitle)
            : [...checked, sub.layerTitle];
          setChecked(updated);
          addNewLayer(updated);
        };
        return (
          <div key={sub.layerTitle} className="flex items-center h-6">
            <StyledCheckbox
              checked={isChecked}
              onCheckedChange={toggle}
              onClick={(e) => e.stopPropagation()}
              className="p-0.25 mr-0.5"
            />
            {sub.color && (
              <span
                className="inline-block w-3 h-0.75 rounded-sm mr-0.75 flex-shrink-0"
                style={{ backgroundColor: sub.color }}
              />
            )}
            <Typography variant="body4">{sub.displayTitle}</Typography>
          </div>
        );
      })}
    </div>
  );
}

function TreeNodeComponent({
  node,
  checked,
  voltageChecked,
  onCheck,
  onVoltageToggle,
  setChecked,
  addNewLayer,
  onChartClick,
}: TreeNodeComponentProps) {
  const visibleLeafIds = leafIds(node);
  const isLeaf = !node.children || node.children.length === 0;
  const hasVoltageOptions =
    isLeaf && !!node.voltageOptions?.length && !!node.layerName;

  const hasActiveVoltageFilter =
    hasVoltageOptions &&
    (voltageChecked[node.layerName!]?.size ?? 0) > 0 &&
    !checked.includes(node.value);

  const subLayerIds = [
    ...(node.subLayers?.map((s) => s.layerTitle) ?? []),
    ...(node.voltageSubLayers?.map((s) => s.layerTitle) ?? []),
  ];
  const allIds = [...visibleLeafIds, ...subLayerIds];

  const isChecked =
    hasActiveVoltageFilter
      ? false
      : allIds.every((id) => checked.includes(id)) &&
        (isLeaf ||
          !node.children?.some((child) =>
            hasActiveDescendantFilter(child, checked, voltageChecked)
          ));

  const isIndeterminate =
    !isChecked &&
    (hasActiveVoltageFilter ||
      allIds.some((id) => checked.includes(id)) ||
      (!isLeaf &&
        node.children?.some((child) =>
          hasActiveDescendantFilter(child, checked, voltageChecked)
        )));

  const onChange = () => {
    if (isIndeterminate) {
      onCheck(node, true);
      return;
    }
    onCheck(node, !isChecked);
  };

  return (
    <TreeItem
      key={node.value}
      nodeId={node.value}
      label={
        <div onClick={(e) => e.stopPropagation()}>
          <label
            className="flex items-center gap-x-1.5 cursor-pointer py-2"
            onClick={(e) => e.stopPropagation()}
          >
            <StyledCheckbox
              checked={isIndeterminate ? "indeterminate" : isChecked}
              onCheckedChange={onChange}
              onClick={(e) => e.stopPropagation()}
              className="p-0.25"
            />
            <Typography variant="body4">{node.label}</Typography>
            {node.info && (
              <Tooltip title={node.info} placement="right" arrow>
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "flex", alignItems: "center", cursor: "default" }}
                >
                  <Info size={13} color="#6B7280" />
                </span>
              </Tooltip>
            )}
            {node.chartButtonTooltip && node.layerName && onChartClick && (
              <Tooltip title={node.chartButtonTooltip} placement="right" arrow>
                <button
                  type="button"
                  aria-label={node.chartButtonTooltip}
                  onClick={(e) => {
                    // o botão vive dentro do <label> do checkbox: sem isto o
                    // clique também marcaria/desmarcaria a camada
                    e.preventDefault();
                    e.stopPropagation();
                    onChartClick(node.layerName!);
                  }}
                  className="flex items-center rounded-sm p-0.5 text-[#6B7280] hover:bg-[#f0f0f0] hover:text-[#1f6fb2] cursor-pointer"
                >
                  <BarChart3 size={14} />
                </button>
              </Tooltip>
            )}
          </label>
          {hasVoltageOptions && (
            <VoltageFilterPanel
              layerName={node.layerName!}
              layerTitle={node.value}
              options={node.voltageOptions!}
              groupLabel={node.voltageGroupLabel}
              voltageSubLayers={node.voltageSubLayers}
              checked={checked}
              voltageChecked={voltageChecked}
              onVoltageToggle={onVoltageToggle}
              setChecked={setChecked}
              addNewLayer={addNewLayer}
            />
          )}
          {node.subLayers && node.subLayers.length > 0 && (
            <SubLayerPanel
              subLayers={node.subLayers}
              groupLabel={node.subLayerGroupLabel}
              checked={checked}
              setChecked={setChecked}
              addNewLayer={addNewLayer}
            />
          )}
        </div>
      }
    >
      {node.children &&
        node.children.map((child) => (
          <TreeNodeComponent
            key={child.value}
            node={child}
            checked={checked}
            voltageChecked={voltageChecked}
            onCheck={onCheck}
            onVoltageToggle={onVoltageToggle}
            setChecked={setChecked}
            addNewLayer={addNewLayer}
            onChartClick={onChartClick}
          />
        ))}
    </TreeItem>
  );
}

export function CheckboxTreeView({
  data,
  onVoltageFilterChange,
  onChartClick,
}: CheckboxTreeViewProps) {
  const { addNewLayer, layers, layerCqlFilters, setLayerVoltageFilter } =
    useLayers();
  const [checked, setChecked] = useState<string[]>(layers);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [voltageChecked, setVoltageChecked] = useState<
    Record<string, Set<number | string>>
  >({});

  useEffect(() => {
    setChecked(layers);
  }, [layers]);

  useEffect(() => {
    const leaves = leafNodes(data);
    const visibleLayerTitles = new Set([
      ...leaves.map((l) => l.title),
      ...leaves.flatMap((l) => l.subLayers?.map((s) => s.layerTitle) ?? []),
      ...leaves.flatMap((l) => l.voltageSubLayers?.map((s) => s.layerTitle) ?? []),
    ]);
    const newChecked = checked.filter((id) => visibleLayerTitles.has(id));
    if (newChecked.length !== checked.length) setChecked(newChecked);
  }, [data]);

  useEffect(() => {
    const updated: Record<string, Set<number | string>> = {};
    for (const [layerName, values] of Object.entries(layerCqlFilters)) {
      updated[layerName] = new Set(values);
    }
    setVoltageChecked(updated);
  }, [layerCqlFilters]);

  const nodes = data.map(mapLayerToTreeNode);

  const handleVoltageFilterChange = (
    layerName: string,
    values: (number | string)[]
  ) => {
    const handler = onVoltageFilterChange ?? setLayerVoltageFilter;
    handler(layerName, values);
  };

  const handleCheck = (node: TreeNode, newValue: boolean) => {
    const isChecked = checked.includes(node.value);

    if (!node.children || node.children.length === 0) {
      const subLayerTitles = [
        ...(node.subLayers?.map((s) => s.layerTitle) ?? []),
        ...(node.voltageSubLayers?.map((s) => s.layerTitle) ?? []),
      ];
      const isFullyChecked = isChecked && subLayerTitles.every((t) => checked.includes(t));
      const hasPartialFilter =
        (!!node.layerName && (voltageChecked[node.layerName]?.size ?? 0) > 0) ||
        subLayerTitles.some((t) => checked.includes(t)) ||
        (isChecked && subLayerTitles.length > 0 && !subLayerTitles.every((t) => checked.includes(t)));
      if (!hasPartialFilter && isFullyChecked === newValue) return;

      const updatedChecked = newValue
        ? [
            ...checked.filter((id) => id !== node.value),
            node.value,
            ...subLayerTitles.filter((t) => !checked.includes(t)),
          ]
        : checked.filter(
            (id) => id !== node.value && !subLayerTitles.includes(id)
          );

      setChecked(updatedChecked);
      addNewLayer(updatedChecked);

      if (node.layerName) {
        if (!newValue) {
          const newVoltageChecked = { ...voltageChecked };
          delete newVoltageChecked[node.layerName];
          setVoltageChecked(newVoltageChecked);
          handleVoltageFilterChange(node.layerName, []);
        } else {
          handleVoltageFilterChange(node.layerName, []);
        }
      }
    } else {
      const ids = allLeafAndSubLayerIds(node);
      const remaining = checked.filter((id) => !ids.includes(id));
      const updatedChecked = newValue ? [...remaining, ...ids] : remaining;
      setChecked(updatedChecked);
      addNewLayer(updatedChecked);

      if (!newValue) {
        const layerNames = descendantLayerNames(node);
        if (layerNames.length > 0) {
          const newVoltageChecked = { ...voltageChecked };
          for (const ln of layerNames) {
            delete newVoltageChecked[ln];
            handleVoltageFilterChange(ln, []);
          }
          setVoltageChecked(newVoltageChecked);
        }
      }
    }
  };

  const handleVoltageToggle = (
    layerName: string,
    layerTitle: string,
    value: number | string,
    allValues: (number | string)[]
  ) => {
    const parentIsChecked = checked.includes(layerTitle);
    const current =
      parentIsChecked && !voltageChecked[layerName]?.size
        ? new Set<number | string>(allValues)
        : voltageChecked[layerName] ?? new Set<number | string>();
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }

    const selected = Array.from(next);

    if (selected.length === allValues.length) {
      const updatedChecked = checked.includes(layerTitle)
        ? checked
        : [...checked, layerTitle];
      setChecked(updatedChecked);
      addNewLayer(updatedChecked);
      setVoltageChecked({ ...voltageChecked, [layerName]: next });
      handleVoltageFilterChange(layerName, []);
    } else {
      const updatedChecked = checked.filter((id) => id !== layerTitle);
      setChecked(updatedChecked);
      addNewLayer(updatedChecked);
      setVoltageChecked({ ...voltageChecked, [layerName]: next });
      handleVoltageFilterChange(layerName, selected);
    }
  };

  return (
    <TreeViewContainer
      defaultCollapseIcon={<ChevronUp size={20} />}
      defaultExpandIcon={<ChevronDown size={20} />}
      expanded={expanded}
      onNodeToggle={(_, nodeIds) => setExpanded(nodeIds)}
    >
      {nodes.map((node) => (
        <TreeNodeComponent
          key={node.value}
          node={node}
          checked={checked}
          voltageChecked={voltageChecked}
          onCheck={handleCheck}
          onVoltageToggle={handleVoltageToggle}
          setChecked={setChecked}
          addNewLayer={addNewLayer}
          onChartClick={onChartClick}
        />
      ))}
    </TreeViewContainer>
  );
}
